import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { getAtlassianPeopleProfileUrl } from '@confluence/profile';
import { getSpaceLink } from '@confluence/route-manager';
import { CONTEXT_PATH } from '@confluence/named-routes';

import { FeedRecommendationQuery } from './FeedRecommendationQuery.graphql';
import type { FeedRecommendationQuery as FeedRecommendationQueryType } from './__types__/FeedRecommendationQuery';
import { PAGE_SIZE } from './RecommendationTile';

export type RecommendationType = {
	score: number;
	name: string;
	icon: string;
	id: string;
	url: string;
	type: 'space' | 'user';
	title?: string;
};

export const useFeedRecommendations = () => {
	const {
		data,
		loading: recsLoading,
		error: feedRecError,
	} = useQuery<FeedRecommendationQueryType>(FeedRecommendationQuery, {
		errorPolicy: 'all',
		fetchPolicy: 'cache-and-network',
	});

	const [recs, setRecs] = useState<RecommendationType[]>([]);
	const [initialRecSize, setInitialRec] = useState<number>(0);

	const recsDismissed = data?.userPreferences.feedRecommendedUserSettingsDismissTimestamp;
	const userRecs = data?.getRecommendedFeedUserConfig?.recommendedPeople;
	const spaceRecs = data?.getRecommendedFeedUserConfig?.recommendedSpaces;

	const msInMonth = 2629800000;
	const showRecs =
		recsDismissed && recsDismissed !== 'NO_VALUE_SET'
			? Math.floor((new Date().getTime() - new Date(recsDismissed).getTime()) / msInMonth) >= 1
			: recsDismissed === 'NO_VALUE_SET';

	const initialState = showRecs && initialRecSize >= PAGE_SIZE && recs.length > 0;

	const [showFeedRecs, setShowFeedRecs] = useState(initialState);

	if (initialState != showFeedRecs) {
		setShowFeedRecs(initialState);
	}

	useEffect(() => {
		const userRecsArray: RecommendationType[] = [];
		const spaceRecsArray: RecommendationType[] = [];

		if (userRecs) {
			userRecs.forEach((user) => {
				if (user.user && user.user.displayName && user.user.id && user.user.isActive) {
					userRecsArray.push({
						score: user.score,
						name: user.user.displayName,
						icon: user.user.photos?.find((photo) => photo?.isPrimary)?.value || '',
						id: user.user.id,
						url: getAtlassianPeopleProfileUrl(user.user.id, 'feed-alpha', CONTEXT_PATH),
						title: user.user.title || '',
						type: 'user',
					});
				}
			});
		}

		if (spaceRecs) {
			spaceRecs.forEach((space) => {
				if (space.space && space.space.name && space.space.id && space.space.key)
					spaceRecsArray.push({
						score: space.score,
						name: space.space.name,
						icon: space.space.icon?.path || '',
						id: space.space.id,
						url: getSpaceLink(space.space.key),
						type: 'space',
					});
			});
		}

		userRecsArray.sort((a, b) => b.score - a.score);
		spaceRecsArray.sort((a, b) => b.score - a.score);
		const recsArray = userRecsArray.concat(spaceRecsArray);
		setRecs(recsArray);
		setInitialRec(recsArray.length);
	}, [userRecs, spaceRecs, setRecs, setInitialRec]);

	return {
		showFeedRecs,
		setShowFeedRecs,
		recs,
		setRecs,
		recsLoading,
		feedRecError,
	};
};
